import { NULL_TAG } from '@racemap/utilities/consts/events';
import {
  KM_TO_METER,
  METERS_PER_SECOND_TO_KMH,
  METERS_PER_SECOND_TO_KNOTS,
  METERS_PER_SECOND_TO_MPH,
  METER_TO_FATHOM,
  METER_TO_FOOT,
  METER_TO_KM,
  METER_TO_MILE,
  METER_TO_NAUTICAL_MILE,
  MILE_TO_METER,
} from '@racemap/utilities/consts/units';
import {
  FATHOM_TO_METER,
  FOOT_TO_METER,
  NAUTICAL_MILE_TO_METER,
} from '@racemap/utilities/consts/units';
import { velocityUnitByUnitType } from '@racemap/utilities/functions/units';
import { Unit, UnitType } from '@racemap/utilities/types/units';
import { memoize } from 'lodash';
import type { PointerEvent } from 'react';
import tinycolor from 'tinycolor2';
import { shallow } from 'zustand/shallow';
import {
  DAYS,
  DEVICE_PIXEL_RATIO,
  FONT,
  HOURS,
  MINUTES,
  SECONDS,
  type Tuple2,
  type Tuple3,
} from './constants';
import { t, tt } from './i18n';
import type { LatLng, TimeMode, XY } from './store';

export type Bounds = {
  min_lat: number;
  max_lat: number;
  min_lng: number;
  max_lng: number;
};

export const NBSP = ' ';
export const NARROW_NBSP = ' ';

export function noop(): void {
  // pass
}

export function validateColor(color: unknown): boolean {
  if (typeof color !== 'string') return false;
  if ((!color.startsWith('rgba(') && !color.startsWith('rgb(')) || !color.endsWith(')'))
    return false;
  const parsed = tinycolor(color);

  return parsed.isValid();
}

export function easeInOut(t: number): number {
  return t < 0.5 ? 4 * t * t * t : 1 - (-2 * t + 2) ** 3 / 2;
}
export function roundTo(a: number, precision: number): number {
  const exp = 10 ** precision;
  return Math.round(a * exp) / exp;
}

export function floorTime(t: number, stepWidth: number): number {
  return Math.floor(t / stepWidth) * stepWidth;
}
export function ceilTime(t: number, stepWidth: number): number {
  return Math.ceil(t / stepWidth) * stepWidth;
}

export function floorTimeAbsolute(t: number, stepWidth: number): number {
  const date = new Date(t);
  if (stepWidth >= DAYS) {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
  }
  if (stepWidth >= HOURS) {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate(), date.getHours()).getTime();
  }
  if (stepWidth >= MINUTES) {
    return new Date(
      date.getFullYear(),
      date.getMonth(),
      date.getDate(),
      date.getHours(),
      date.getMinutes(),
    ).getTime();
  }
  if (stepWidth >= SECONDS) {
    return new Date(
      date.getFullYear(),
      date.getMonth(),
      date.getDate(),
      date.getHours(),
      date.getMinutes(),
      date.getSeconds(),
    ).getTime();
  }
  return t;
}
export function ceilTimeAbsolute(t: number, stepWidth: number): number {
  return floorTimeAbsolute(t + stepWidth, stepWidth);
}

export function findAllIndexes<T>(
  array: Array<T>,
  pred: (item: T, index: number, array: Array<T>) => boolean,
): Array<number> {
  const indexes = [];
  for (let i = 0; i < array.length; i++) {
    if (pred(array[i], i, array)) {
      indexes.push(i);
    }
  }
  return indexes;
}

export function iteratorFind<T>(
  iter: Iterator<T, unknown, unknown>,
  pred: (item: T) => boolean,
): T | null {
  let result = iter.next();
  while (!result.done) {
    if (pred(result.value)) {
      return result.value;
    }
    result = iter.next();
  }

  return null;
}

export function realSubstring(str: string, start: number, end: number): string {
  return [...str].slice(start, end).join('');
}

export function make2DCanvas(
  width: number,
  height: number,
  scale = DEVICE_PIXEL_RATIO,
  font = FONT,
): { canvas: HTMLCanvasElement; context: CanvasRenderingContext2D } {
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext('2d');
  if (context == null) {
    throw new Error("Couldn't create drawing canvas");
  }
  if (scale > 1) {
    context.scale(scale, scale);
  }
  context.font = font;
  return { canvas, context };
}

const getDummyWebGLContext = memoize((): WebGLRenderingContext | null => {
  const canvas = document.createElement('canvas');
  canvas.style.width = '0px';
  canvas.style.height = '0px';
  canvas.width = 1;
  canvas.height = 1;
  if (document.body == null) {
    return null;
  }
  document.body.appendChild(canvas);
  let gl = null;
  try {
    gl = canvas.getContext('webgl');
  } catch {
    gl = null;
  }
  if (gl === null) {
    try {
      gl = canvas.getContext('experimental-webgl');
    } catch {
      gl = null;
    }
  }
  return gl as WebGLRenderingContext | null;
});

export function detectWebGL(): boolean {
  return getDummyWebGLContext() != null;
}

export function detectWebGLMaxTextureSize(): number {
  const gl = getDummyWebGLContext();
  if (gl != null) {
    return gl.getParameter(gl.MAX_TEXTURE_SIZE);
  }
  return 0;
}

export function leftpad2(num: number): string {
  return num > 9 ? `${num}` : `0${num}`;
}

function formatPositiveTimeDuration(time: number, showSeconds = true): string {
  if (!Number.isFinite(time)) return '';
  if (time === 0) {
    return showSeconds ? `00h${NBSP}00m${NBSP}00s` : `00h${NBSP}00m`;
  }
  const sec = Math.floor(time / 1000);
  const min = Math.floor(sec / 60);
  const hour = Math.floor(min / 60);

  return showSeconds
    ? `${leftpad2(hour)}h${NBSP}${leftpad2(min % 60)}m${NBSP}${leftpad2(sec % 60)}s`
    : `${leftpad2(hour)}h${NBSP}${leftpad2(min % 60)}m`;
}

export function formatTimeDuration(time: number, showSeconds = true): string {
  if (!Number.isFinite(time)) return '-';
  if (time < 0) {
    return `-${formatPositiveTimeDuration(-time, showSeconds)}`;
  }
  return formatPositiveTimeDuration(time, showSeconds);
}

export function formatTimeDurationHuman(time: number): string {
  if (!Number.isFinite(time)) return '';
  if (time <= 1 * SECONDS) {
    return t('seen.now');
  }
  if (time < 1 * MINUTES) {
    return t('seen.lessmin');
  }
  if (time < 1 * HOURS) {
    return tt('seen.min', Math.round(time / MINUTES).toString());
  }
  return tt('seen.hour', Math.round(time / HOURS).toString());
}

export function formatTime(time: number, showSeconds = true): string {
  if (time === 0) {
    return showSeconds ? `00h${NBSP}00m${NBSP}00s` : `00h${NBSP}00m`;
  }
  const date = new Date(time);

  const min = date.getMinutes();
  const hour = date.getHours();
  const sec = date.getSeconds();

  return showSeconds
    ? `${leftpad2(hour)}h${NBSP}${leftpad2(min)}m${NBSP}${leftpad2(sec)}s`
    : `${leftpad2(hour)}h${NBSP}${leftpad2(min)}m`;
}

export function formatDate(time: number): string {
  const date = new Date(time);

  const year = date.getFullYear();
  const month = date.getMonth() + 1;
  const day = date.getDate();

  return `${year}-${leftpad2(month)}-${leftpad2(day)}`;
}

/**
 * A time cursor as the player's time mode presents it: elapsed since the event start, or the time of
 * day. A multi-day event gets the date too — a bare clock time would not say which day.
 */
export function formatCursorTime(
  time: number,
  startTime: number,
  endTime: number,
  timeMode: TimeMode,
): string {
  if (timeMode === 'RELATIVE') return formatTimeDuration(time - startTime);

  return formatDate(startTime) === formatDate(endTime)
    ? formatTime(time)
    : `${formatDate(time)}${NBSP}${formatTime(time)}`;
}

export function formatSpeed(speed: number, unitType: UnitType): string {
  if (Number.isNaN(speed)) {
    return '-';
  }
  switch (unitType) {
    case UnitType.METRIC:
      return `${(speed * METERS_PER_SECOND_TO_KMH).toFixed(1)}`;
    case UnitType.IMPERIAL:
      return `${(speed * METERS_PER_SECOND_TO_MPH).toFixed(1)}`;
    case UnitType.NAUTICAL:
      return `${(speed * METERS_PER_SECOND_TO_KNOTS).toFixed(1)}`;
    default:
      return '-';
  }
}

export function formatSpeedWithUnit(speed: number, unitType: UnitType): string {
  return `${formatSpeed(speed, unitType)}${NBSP}${velocityUnitByUnitType(unitType)}`;
}

export function formatAsKM(meters: number): string {
  return `${(meters / 1000).toFixed(1)}${NBSP}${Unit.Distance.km}`;
}
export function formatMI(meters: number): string {
  return `${(meters * METER_TO_MILE).toFixed(1)}${NBSP}${Unit.Distance.mi}`;
}
export function formatNMI(meters: number): string {
  return `${(meters * METER_TO_NAUTICAL_MILE).toFixed(1)}${NBSP}${Unit.Distance.nmi}`;
}
export function formatElevation(elevation: number, unitType: UnitType): string {
  if (Number.isNaN(elevation)) return '-';
  switch (unitType) {
    case UnitType.METRIC:
      return `${formatNumber(elevation)}${NBSP}${Unit.Distance.m}`;
    case UnitType.IMPERIAL:
      return `${formatNumber(elevation * METER_TO_FOOT)}${NBSP}${Unit.Distance.ft}`;
    case UnitType.NAUTICAL:
      return `${formatNumber(elevation * METER_TO_FATHOM)}${NBSP}${Unit.Distance.ftm}`;
    default:
      return '-';
  }
}
export function formatDistance(distance: number, unitType: UnitType): string {
  if (Number.isNaN(distance)) return '-';
  switch (unitType) {
    case UnitType.NAUTICAL:
      return formatNMI(distance);
    case UnitType.METRIC:
      return formatAsKM(distance);
    case UnitType.IMPERIAL:
      return formatMI(distance);
    default:
      return '-';
  }
}
export function formatNumber(num: number): string {
  return Math.round(num)
    .toString()
    .replace(/\B(?=(\d{3})+(?!\d))/g, NARROW_NBSP);
}

export function remove<T>(array: Array<T>, item: T): void {
  const index = array.indexOf(item);
  if (index >= 0) {
    array.splice(index, 1);
  }
}

export function arrayMove<T>(array: Array<T>, moveIndex: number, toIndex: number): Array<T> {
  // From: https://github.com/granteagon/move/blob/master/src/index.js
  const item = array[moveIndex];
  const length = array.length;
  const diff = moveIndex - toIndex;

  if (diff > 0) {
    // move left
    return [
      ...array.slice(0, toIndex),
      item,
      ...array.slice(toIndex, moveIndex),
      ...array.slice(moveIndex + 1, length),
    ];
  }
  if (diff < 0) {
    // move right
    const targetIndex = toIndex + 1;
    return [
      ...array.slice(0, moveIndex),
      ...array.slice(moveIndex + 1, targetIndex),
      item,
      ...array.slice(targetIndex, length),
    ];
  }
  return array;
}

export function inBounds(needle: XY, { NE, SW }: { NE: XY; SW: XY }): boolean {
  return SW.x <= needle.x && needle.x <= NE.x && NE.y <= needle.y && needle.y <= SW.y;
}

export function toggleThroughArray<T>(array: Array<T>, current: T): T {
  return array[(array.indexOf(current) + 1) % array.length];
}

export function fromGeoJSONLatLng([lng, lat]: [number, number] | [number, number, number]): LatLng {
  return { lat, lng };
}

export function detectIE(): false | number {
  // Source: http://codepen.io/gapcode/pen/vEJNZN
  const ua = window.navigator.userAgent;

  const msie = ua.indexOf('MSIE ');
  if (msie > 0) {
    // IE 10 or older => return version number
    return Number.parseInt(ua.substring(msie + 5, ua.indexOf('.', msie)), 10);
  }

  const trident = ua.indexOf('Trident/');
  if (trident > 0) {
    // IE 11 => return version number
    const rv = ua.indexOf('rv:');
    return Number.parseInt(ua.substring(rv + 3, ua.indexOf('.', rv)), 10);
  }

  const edge = ua.indexOf('Edge/');
  if (edge > 0) {
    // Edge (IE 12+) => return version number
    return Number.parseInt(ua.substring(edge + 5, ua.indexOf('.', edge)), 10);
  }

  // other browser
  return false;
}

export function getBoundsFromCoordinates(points: Array<Tuple2 | Tuple3>): Bounds {
  const bounds = {
    min_lat: Number.POSITIVE_INFINITY,
    max_lat: Number.NEGATIVE_INFINITY,
    min_lng: Number.POSITIVE_INFINITY,
    max_lng: Number.NEGATIVE_INFINITY,
  };
  for (let i = 0, len = points.length; i < len; i++) {
    const [lng, lat] = points[i];
    bounds.min_lat = Math.min(lat, bounds.min_lat);
    bounds.max_lat = Math.max(lat, bounds.max_lat);
    bounds.min_lng = Math.min(lng, bounds.min_lng);
    bounds.max_lng = Math.max(lng, bounds.max_lng);
  }
  return bounds;
}

export function sleep(timeout: number): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, timeout);
  });
}

export function median(values: Array<number>): number {
  const sortedValues = values.slice(0).sort();
  const half = Math.floor(sortedValues.length / 2);
  const isEven = sortedValues.length % 2 === 0;
  return isEven ? (sortedValues[half] + sortedValues[half - 1]) / 2 : sortedValues[half];
}

export function mean(values: Iterable<number>): number {
  let sum = 0;
  let count = 0;
  for (const value of values) {
    sum += value;
    count += 1;
  }
  return sum / count;
}

/**
 * Quickly estimates the distance between two geographic coordinates (longitude/latitude) in meters.
 *
 * This function uses a fast approximation based on the equirectangular projection,
 * which is computationally cheaper than the Haversine formula but less accurate for large distances or near the poles.
 * It is suitable for applications where performance is more important than perfect accuracy,
 * such as rendering map scales or processing large datasets.
 *
 * @param xLng - Longitude of the first point (in degrees)
 * @param xLat - Latitude of the first point (in degrees)
 * @param yLng - Longitude of the second point (in degrees)
 * @param yLat - Latitude of the second point (in degrees)
 * @returns The approximate distance between the two points, in meters.
 *
 * @example
 * const d = fastDistance(13.4050, 52.5200, 2.3522, 48.8566); // Berlin to Paris
 * console.log(d); // ~878000 (meters)
 */
export function fastDistance(xLng: number, xLat: number, yLng: number, yLat: number): number {
  const degrees2radians = Math.PI / 180.0;
  const dLat = degrees2radians * (yLat - xLat);
  const dLon = degrees2radians * (yLng - xLng);
  const dX = dLon * Math.cos(0.5 * degrees2radians * (yLat + xLat));
  const dY = dLat;
  return 6373000 * Math.sqrt(dX * dX + dY * dY);
}

export function* meanFilter(
  array: Array<{ x: number; y: number }>,
  targetSize: number,
): Generator<{ x: number; y: number }, void, void> {
  const step = array.length / targetSize;
  for (let i = 0; i < array.length; i += step) {
    const _i = Math.floor(i);
    let sum = 0;
    let count = 0;
    for (let j = _i; j < i + step && j < array.length; j++) {
      sum += array[j].y;
      count++;
    }
    yield { x: array[_i].x, y: sum / count };
  }
}

export function* clampYFilter(
  array: Iterable<{ x: number; y: number }>,
  minY: null | number,
  maxY: null | number,
): Generator<{ x: number; y: number }, void, void> {
  for (const xy of array) {
    let y = xy.y;
    if (minY != null) {
      y = Math.max(minY, y);
    }
    if (maxY != null) {
      y = Math.min(maxY, y);
    }
    yield {
      x: xy.x,
      y,
    };
  }
}

export function matchesTagSelection(
  starterTags: Map<string, string>,
  selectedTags: Map<string, Array<string>>,
): boolean {
  return Array.from(selectedTags).every(
    ([key, values]) =>
      (values.includes(NULL_TAG) && !starterTags.has(key)) ||
      (starterTags.has(key) && values.includes(starterTags.get(key) || '')),
  );
}

export function longestCommonPrefix(array: Array<string>): string {
  if (array.length < 2) return '';
  const A = array.concat().sort();
  const a1 = A[0];
  const a2 = A[A.length - 1];
  const L = a1.length;
  let i = 0;
  while (i < L && a1.charAt(i) === a2.charAt(i)) i++;
  return a1.substring(0, i);
}

export function fp64ify(a: number, out: Tuple2 = [0, 0]): Tuple2 {
  const hiPart = Math.fround(a);
  const loPart = a - hiPart;
  out[0] = hiPart;
  out[1] = loPart;
  return out;
}

export function memoizeSelector<Arg0, CacheVal, RetVal>(
  func: (arg0: Arg0) => RetVal,
  cacheExtractor: (arg0: Arg0) => CacheVal,
): (arg0: Arg0) => RetVal {
  let cache: { value: RetVal; cacheKey: CacheVal } | null = null;
  return (arg0: Arg0): RetVal => {
    const cacheKey = cacheExtractor(arg0);
    if (cache != null && shallow(cacheKey, cache.cacheKey)) {
      return cache.value;
    }
    const value = func(arg0);
    cache = { value, cacheKey };
    return value;
  };
}

export function singleMemoize<Arg0, RetVal>(func: (arg0: Arg0) => RetVal): (arg0: Arg0) => RetVal {
  let cache: { arg0: Arg0; value: RetVal } | null = null;
  return (arg0: Arg0): RetVal => {
    if (cache != null && cache.arg0 === arg0) {
      return cache.value;
    }
    const value = func(arg0);
    cache = { arg0, value };
    return value;
  };
}

export function singleMemoize2<Arg0, Arg1, RetVal>(
  func: (arg0: Arg0, arg1: Arg1) => RetVal,
): (arg0: Arg0, arg1: Arg1) => RetVal {
  let cache: { arg0: Arg0; arg1: Arg1; value: RetVal } | null = null;
  return (arg0: Arg0, arg1: Arg1): RetVal => {
    if (cache != null && cache.arg0 === arg0 && cache.arg1 === arg1) {
      return cache.value;
    }
    const value = func(arg0, arg1);
    cache = { arg0, arg1, value };
    return value;
  };
}

function addFront<K>(lru: Array<K>, item: K) {
  const index = lru.indexOf(item);
  if (index >= 0) {
    lru.splice(index, 1);
  }
  lru.unshift(item);
}

function reduce<T, V>(iter: Iterator<T, T, unknown>, func: (acc: V, item: T) => V, initial: V): V {
  let acc = initial;

  let iterResult = iter.next();
  while (!iterResult.done) {
    acc = func(acc, iterResult.value);
    iterResult = iter.next();
  }
  return acc;
}

export function countIf<T>(arr: Array<T>, pred: (item: T) => boolean): number {
  return arr.reduce((r, a) => r + (pred(a) ? 1 : 0), 0);
}

export function union<T>(a: Set<T>, b: Set<T>): Set<T> {
  // @ts-expect-error: Set has a union method
  if (a.union != null) {
    // @ts-expect-error: Set has a union method
    return a.union(b);
  }
  /* tslint:enable */
  return new Set([...a, ...b]);
}
export function difference<T>(a: Set<T>, b: Set<T>): Set<T> {
  // @ts-expect-error: Set has a difference method
  if (a.difference != null) {
    // @ts-expect-error: Set has a difference method
    return a.difference(b);
  }
  return new Set([...a].filter((x) => !b.has(x)));
}

export function intersection<T>(a: Set<T>, b: Set<T>): Set<T> {
  // @ts-expect-error: Set has a difference method
  if (a.intersection != null) {
    // @ts-expect-error: Set has a difference method
    return a.intersection(b);
  }
  return new Set([...a].filter((x) => b.has(x)));
}

export class BoundedMap<K, V> {
  // Front item is the least recently used
  lru: Array<K> = [];
  map: Map<K, V> = new Map();
  max: number;

  constructor(max: number) {
    this.max = max;
  }

  get(key: K): V | null {
    const value = this.map.get(key);
    if (value != null) {
      addFront(this.lru, key);
    }
    return value || null;
  }
  has(key: K): boolean {
    return this.map.has(key);
  }
  set(key: K, value: V): void {
    addFront(this.lru, key);
    this.map.set(key, value);
    if (this.lru.length > this.max) {
      const key = this.lru.pop();
      if (key !== undefined) this.map.delete(key);
    }
  }
  delete(key: K): void {
    const index = this.lru.indexOf(key);
    if (index >= 0) {
      this.lru.splice(index, 1);
    }
    this.map.delete(key);
  }
}

export class BoundedBufferMap<K> {
  // Front item is the least recently used
  lru: Array<K> = [];
  map: Map<K, ArrayBufferLike> = new Map();
  maxBytes: number;

  constructor(maxBytes: number) {
    this.maxBytes = maxBytes;
  }

  get(key: K): ArrayBufferLike | null {
    const value = this.map.get(key);
    if (value != null) {
      addFront(this.lru, key);
    }
    return value || null;
  }
  has(key: K): boolean {
    return this.map.has(key);
  }
  set(key: K, value: ArrayBufferLike): void {
    addFront(this.lru, key);
    this.map.set(key, value);
    while (this.byteLength > this.maxBytes && this.map.size > 1) {
      const key = this.lru.pop();
      if (key !== undefined) this.map.delete(key);
    }
  }
  delete(key: K): void {
    const index = this.lru.indexOf(key);
    if (index >= 0) {
      this.lru.splice(index, 1);
    }
    this.map.delete(key);
  }
  get byteLength(): number {
    return reduce(this.map.values(), (r, a) => r + (a?.byteLength || 0), 0);
  }
}

export class Deferred<T> {
  resolve: null | ((arg0: T) => void);
  reject: null | ((arg0: Error) => void);
  promise: Promise<T>;

  constructor() {
    this.resolve = null;
    this.reject = null;

    this.promise = new Promise((resolve, reject) => {
      this.resolve = resolve;
      this.reject = reject;
    });
  }
}

export class AsyncQueue<ID, V> {
  max: number;
  // The queue is read from front
  queue: Array<{
    id: ID;
    func: () => Promise<V>;
    deferred: Deferred<V>;
    isRunning: boolean;
  }> = [];

  constructor(max = 5) {
    this.max = max;
  }

  async run(): Promise<void> {
    while (this.queue.length > 0 && this.current() == null) {
      const item = this.queue[0];
      item.isRunning = true;
      try {
        const res = await item.func();
        item.deferred.resolve?.(res);
        if (this.queue.includes(item)) {
          this.queue.splice(this.queue.indexOf(item), 1);
        }
      } finally {
        item.isRunning = false;
      }
    }
  }

  current(): ID | null {
    const item = this.queue.find((a) => a.isRunning);
    return item != null ? item.id : null;
  }

  has(id: ID): boolean {
    return this.queue.some((a) => a.id === id);
  }

  add(id: ID, func: () => Promise<V>, { force = false }: { force?: boolean } = {}): Promise<V> {
    const queueItemIndex = this.queue.findIndex((item) => item.id === id);
    if (!force && queueItemIndex !== -1) {
      return this.queue[queueItemIndex].deferred.promise;
    }
    if (force && queueItemIndex !== -1) {
      this.queue[queueItemIndex].deferred.reject?.(new Error('aborted'));
      this.queue.splice(queueItemIndex, 1);
    }
    const deferred = new Deferred<V>();
    this.queue.unshift({ id, deferred, func, isRunning: false });
    while (this.queue.length > this.max) {
      this.queue.pop();
    }
    this.run();
    return deferred.promise;
  }
}

export function queryize(
  url: string,
  query: Record<string, string | number | boolean | null> = {},
): string {
  let output = url;
  const queryString = Object.entries(query)
    .filter(([, value]) => value != null)
    .map(([key, value]) => `${key}=${value}`)
    .join('&');
  if (queryString !== '') {
    output += `?${queryString}`;
  }
  return output;
}

export function onlyMouseEvents(
  fn: (ev: PointerEvent<Element>) => void,
): (ev: PointerEvent<Element>) => void {
  return (ev) => {
    if (ev.pointerType === 'mouse') fn(ev);
  };
}

let _hasMouse = false;
if (typeof window !== 'undefined') {
  const _hasMouseHandler = (ev: globalThis.PointerEvent) => {
    if (ev.pointerType === 'mouse') {
      _hasMouse = true;
      window.document.body.removeEventListener('pointermove', _hasMouseHandler, false);
      window.document.body.removeEventListener('pointerenter', _hasMouseHandler, false);
    }
  };
  window.document.body.addEventListener('pointermove', _hasMouseHandler, false);
  window.document.body.addEventListener('pointerenter', _hasMouseHandler, false);
}
export function hasMouse(): boolean {
  return _hasMouse;
}

export type Mutable<T> = {
  -readonly [K in keyof T]: T[K];
};

export function meterToDistanceUnitFactor(unit: UnitType) {
  switch (unit) {
    case UnitType.IMPERIAL:
      return METER_TO_MILE;
    case UnitType.NAUTICAL:
      return METER_TO_NAUTICAL_MILE;
    case UnitType.METRIC:
      return METER_TO_KM;
    default:
      return 1;
  }
}

export function distanceUnitToMeterFactor(unit: UnitType) {
  switch (unit) {
    case UnitType.IMPERIAL:
      return MILE_TO_METER;
    case UnitType.NAUTICAL:
      return NAUTICAL_MILE_TO_METER;
    case UnitType.METRIC:
      return KM_TO_METER;
    default:
      return 1;
  }
}

export function convertMeterToElevationUnit(unitType: UnitType) {
  switch (unitType) {
    case UnitType.IMPERIAL:
      return METER_TO_FOOT;
    case UnitType.NAUTICAL:
      return METER_TO_FATHOM;
    case UnitType.METRIC:
      return 1;
    default:
      return 1;
  }
}

export function convertElevationUnitToMeter(unitType: UnitType) {
  switch (unitType) {
    case UnitType.IMPERIAL:
      return FOOT_TO_METER;
    case UnitType.NAUTICAL:
      return FATHOM_TO_METER;
    case UnitType.METRIC:
      return 1;
    default:
      return 1;
  }
}
